import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  AppBar,
  Toolbar,
  Fade,
  Slide,
  Grow,
  Zoom,
  Menu,
  MenuItem,
  IconButton,
  Badge,
} from '@mui/material'
import {
  Person,
  Bookmark,
  Chat,
  Notifications,
  AccountCircle,
  Dashboard,
  Logout,
  ArrowForward,
  FavoriteBorder,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/index'
import { logout } from '@store/slices/authSlice'
import { SearchBar } from '../components/search'
import { UserRole } from '../types/index'
import SearchBarInPatientLanding from './SearchForPatientLanding'

const accentColor = '#3BAE78'
const accentHoverColor = '#2F8E61'

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const dispatch = useAppDispatch()
  const { user, isAuthenticated } = useAppSelector(state => state.auth)

  const [searchLocation, setSearchLocation] = React.useState<string>('')
  const [insurance, setInsurance] = useState<string | null>(null)
  const [specialization, setSpecialization] = useState<string | null>(null)
  const [searchRadius, setSearchRadius] = useState<string | null>('')

  const [accountMenuAnchor, setAccountMenuAnchor] =
    useState<null | HTMLElement>(null)

  const handleLoginClick = () => {
    navigate('/patient-login', {
      state: {
        from: {
          pathname: `${routerLocation.pathname}${routerLocation.search}`,
        },
      },
    })
  }

  const handleGetStartedClick = () => {
    navigate('/patient-create-account')
  }

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null)
  }

  const handleDashboardClick = () => {
    handleAccountMenuClose()
    navigate('/dashboard')
  }

  const handleLogoutClick = () => {
    handleAccountMenuClose()
    dispatch(logout())
    navigate('/patient-login')
  }

  const handleSearch = () => {
    const params = new URLSearchParams()
    if (searchLocation?.trim()) {
      params.set('location', searchLocation.trim())
    }
    if (specialization?.trim()) {
      params.set('specialization', specialization.trim())
    }
    if (insurance?.trim()) {
      params.set('insurance', insurance.trim())
    }
    params.set('tab', 'jobs')

    // navigate(`/find-therapists?${params.toString()}`)
    if (user?.role === UserRole.CLIENT) {
      navigate(`/find-therapists`, { state: { fromLandingPage: true } })
    } else {
      navigate(`/find-therapist`, { state: { fromLandingPage: true } })
    }
  }

  const isTherapist = user?.role === UserRole.THERAPIST
  const isClient = user?.role === UserRole.CLIENT

  return (
    <Box
      sx={{
        height: '100vh',
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Fade in timeout={800}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            height: { xs: '64px', md: '72px' },
            flexShrink: 0,
          }}
        >
          <Toolbar
            sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
          >
            <Slide direction="right" in timeout={1000}>
              <Box
                component="img"
                src="/logo.svg"
                alt="PillarThera"
                sx={{ height: { xs: 32, sm: 48, md: 52 }, cursor: 'pointer' }}
                onClick={() => navigate('/')}
              />
            </Slide>

            {isAuthenticated ? (
              <Slide
                direction="left"
                in
                timeout={1000}
                style={{ transitionDelay: '200ms' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isTherapist && (
                    <IconButton
                      onClick={() => navigate('/saved-jobs')}
                      sx={{
                        color: 'grey.600',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: accentColor,
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <Bookmark />
                    </IconButton>
                  )}
                  {(isTherapist || isClient) && (
                    <IconButton
                      onClick={() => navigate('/messages')}
                      sx={{
                        color: 'grey.600',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: accentColor,
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <Chat />
                    </IconButton>
                  )}
                  <IconButton
                    sx={{
                      color: 'grey.600',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: accentColor,
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <Badge badgeContent={3} color="info">
                      <Notifications />
                    </Badge>
                  </IconButton>
                  <IconButton
                    onClick={handleAccountMenuOpen}
                    sx={{
                      color: 'grey.600',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: accentColor,
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <AccountCircle />
                  </IconButton>
                </Box>
              </Slide>
            ) : (
              <Slide
                direction="left"
                in
                timeout={1000}
                style={{ transitionDelay: '200ms' }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={handleLoginClick}
                  sx={{
                    color: accentColor,
                    borderColor: accentColor,
                    borderRadius: 2,
                    px: { sm: 2, md: 3 },
                    py: 1,
                    fontSize: { sm: '0.75rem', md: '0.875rem' },
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: accentColor,
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(59, 174, 120, 0.3)',
                    },
                  }}
                >
                  Sign Up / Login
                </Button>
              </Slide>
            )}
          </Toolbar>
        </AppBar>
      </Fade>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Slide
          direction="right"
          in
          timeout={1000}
          style={{ transitionDelay: '300ms' }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { xs: 2, sm: 3, md: 4, lg: 6 },
              py: { xs: 1.5, sm: 2, md: 3 },
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Fade in timeout={800} style={{ transitionDelay: '500ms' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: {
                    xs: '1.5rem',
                    sm: '2rem',
                    md: '2.25rem',
                    lg: '2.5rem',
                  },
                  fontWeight: 700,
                  color: 'grey.900',
                  lineHeight: 1.2,
                  mb: { xs: 1.5, md: 2 },
                }}
              >
                Find the Right{' '}
                <Box component="span" sx={{ color: accentColor }}>
                  Provider
                </Box>
                , Anytime, Anywhere
              </Typography>
            </Fade>

            <Fade in timeout={800} style={{ transitionDelay: '900ms' }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  mb: { xs: 2, md: 2.5 },
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleGetStartedClick}
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: accentColor,
                    borderRadius: 999,
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1, md: 1.25 },
                    fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: accentHoverColor,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px rgba(59, 174, 120, 0.35)',
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Fade>

            <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
              <Fade in timeout={800} style={{ transitionDelay: '1500ms' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'grey.800',
                    mb: { xs: 1, md: 1.5 },
                    fontWeight: 600,
                    fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
                  }}
                >
                  Trusted by families and providers nationwide
                </Typography>
              </Fade>

              <Grid
                container
                spacing={{ xs: 1, sm: 1.5, md: 2 }}
                sx={{ maxWidth: { md: '450px', lg: '500px' } }}
              >
                {[
                  { value: '500+', label: 'Specialties covered' },
                  { value: '1,200+', label: 'Licensed providers' },
                  { value: '400+', label: 'Insurance partners' },
                  { value: '50k+', label: 'Sessions facilitated' },
                ].map((stat, index) => (
                  <Grid item xs={6} key={stat.label}>
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: `${1700 + index * 200}ms` }}
                    >
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 'bold',
                            color: 'grey.800',
                            mb: 0.25,
                            fontSize: {
                              xs: '1.125rem',
                              sm: '1.25rem',
                              md: '1.5rem',
                            },
                            transition: 'transform 0.3s ease, color 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              color: accentColor,
                            },
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'grey.600',
                            lineHeight: 1.2,
                            fontSize: {
                              xs: '0.625rem',
                              sm: '0.6875rem',
                              md: '0.75rem',
                            },
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Slide>

        <Zoom in timeout={1200} style={{ transitionDelay: '400ms' }}>
          <Box
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              position: 'relative',
              display: { xs: 'none', md: 'block' },
              borderRadius: { md: 3 },
              overflow: 'hidden',
              height: { xs: '200px', md: '100%' },
              boxShadow: '0 20px 60px rgba(59, 174, 120, 0.25)',
            }}
          >
            <Box
              component="img"
              src="/therapy.jpg"
              alt="Therapy session"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.03)',
                },
              }}
            />
          </Box>
        </Zoom>
      </Box>

      {/* Search Bar Section - Full Width Bottom */}
      <Box
        sx={{
          width: '100%',
          bgcolor: 'grey.50',
          py: { xs: 2, md: 2.5 },
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          borderTop: '1px solid',
          borderColor: 'grey.200',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', sm: '900px', md: '1100px', lg: '1200px' },
            mx: 'auto',
          }}
        >
          <Fade in timeout={800} style={{ transitionDelay: '1100ms' }}>
            <Typography
              variant="h6"
              sx={{
                color: 'grey.800',
                fontWeight: 600,
                mb: { xs: 1.5, md: 2 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              }}
            >
              Find affordable care here
            </Typography>
          </Fade>

          <Grow in timeout={1000} style={{ transitionDelay: '1300ms' }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ width: '100%', maxWidth: '100%' }}>
                <SearchBarInPatientLanding
                  location={searchLocation}
                  radius={searchRadius}
                  insurance={insurance}
                  onRadiusChange={setSearchRadius}
                  specialization={specialization}
                  onLocationChange={setSearchLocation}
                  onInsuranceChange={setInsurance}
                  onSpecializationChange={setSpecialization}
                  onSearch={handleSearch}
                  showSearchButton
                  disableHoverExpand
                  accentColor={accentColor}
                  accentHoverColor={accentHoverColor}
                  thirdFieldLabel="Needs"
                  thirdFieldPlaceholder="What support are you seeking?"
                  thirdFieldIcon={FavoriteBorder}
                  needs={''}
                  onNeedsChange={function (value: string | null): void {
                    throw new Error('Function not implemented.')
                  }}
                />
              </Box>
            </Box>
          </Grow>
        </Box>
      </Box>

      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            minWidth: 200,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={handleDashboardClick}>
          <Dashboard sx={{ mr: 2, fontSize: '1.2rem' }} />
          Dashboard
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <Logout sx={{ mr: 2, fontSize: '1.2rem' }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default LandingPage
